/**
 * Decides what to do with a build that is sitting in the service worker's
 * waiting state.
 *
 * The question that matters is "would a reload cost the user anything right
 * now?" — not "how long ago did the document load". An earlier version of this
 * policy used document age (auto-apply only within 15s of load) and it missed
 * the exact population it was written for:
 *
 *  - A user returning after weeks has every asset changed, so the new worker
 *    has the whole precache to download before it reaches `waiting`. Crossing
 *    the window was normal, not exceptional — the longer someone had been away,
 *    the more certain they were to miss it.
 *  - Sign-in is a route inside this SPA, so a returning user types credentials
 *    within seconds of load. That tripped the "user has entered something"
 *    guard, which then stayed tripped for the life of the document.
 *  - Resuming the installed PWA (or a tab left open for days) is not a document
 *    load at all, so its age was hours. The one moment that most deserves a
 *    silent update could only ever prompt.
 *
 * So the decision is made from what is actually on screen, and it is made
 * repeatedly rather than once: every natural break re-asks the question. A
 * build we cannot apply now is applied at the next safe moment instead of
 * waiting behind a button the user has to notice.
 */

/** Two auto-reloads closer together than this are a loop, not two deploys. */
export const AUTO_RELOAD_GUARD_MS = 60_000;

/**
 * Interaction more recent than this means the user has their hands on the page.
 * We hold the update rather than reloading under them; the next break takes it.
 */
export const ACTIVE_INTERACTION_MS = 10_000;

const AUTO_RELOAD_MARKER = 'pwa-auto-reload-at';

/**
 * Why we are re-asking.
 *
 * A navigation replaces the screen regardless, so a pending update rides along
 * with it. `detected` is the update merely turning up while the user is on a
 * page, which waits for them to pause first.
 *
 * There is deliberately no separate `resumed` moment. Resuming the app looks
 * like it deserves one, but it does not: bringing a tab or an installed PWA to
 * the foreground fires no interaction event on the page, so a user who resumes
 * and has not touched anything already reads as idle here and their update
 * applies at once. A `resumed` moment would only ever differ from `detected`
 * for someone who resumed AND is actively interacting — and reloading under
 * their hands is exactly what the quiet period is for. It was tried, and all
 * it bought was provenance state to keep in sync across two kinds of update
 * check, for at most the ~10s that quiet period costs.
 */
export type UpdateMoment = 'detected' | 'navigated';

export type UpdateDecision =
  /** Activate the waiting worker and reload now. */
  | 'apply'
  /** Safe in principle, but the user is mid-interaction. Re-ask shortly. */
  | 'wait'
  /** Something would be lost. Offer the reload and leave the timing to them. */
  | 'prompt';

export interface AutoUpdateContext {
  moment: UpdateMoment;
  /**
   * Something a reload would discard: text entered since the last natural
   * break, or an open dialog / drawer / wizard.
   */
  hasWorkInProgress: boolean;
  /** ms since the user last interacted, or `Infinity` if they never have. */
  msSinceInteraction: number;
  /** Epoch ms of the last auto-reload in this tab, or 0 if there was none. */
  lastAutoReloadAt: number;
  /** Epoch ms now. */
  now: number;
}

export function decideUpdate({
  moment,
  hasWorkInProgress,
  msSinceInteraction,
  lastAutoReloadAt,
  now,
}: AutoUpdateContext): UpdateDecision {
  // A reload moments ago that did NOT clear the waiting worker means activation
  // is not sticking (a half-rolled-out deploy, a broken build). Reloading again
  // would loop with the page never settling, so hand the decision back — the
  // same bounded one-shot reasoning as `utils/reloadOnFails`.
  if (lastAutoReloadAt > 0 && now - lastAutoReloadAt < AUTO_RELOAD_GUARD_MS) {
    return 'prompt';
  }

  if (hasWorkInProgress) {
    return 'prompt';
  }

  // The user is between screens. Nothing is in flight and the view is being
  // replaced regardless, so take the new build now.
  if (moment === 'navigated') {
    return 'apply';
  }

  return msSinceInteraction >= ACTIVE_INTERACTION_MS ? 'apply' : 'wait';
}

export function readLastAutoReloadAt(): number {
  try {
    return Number(window.sessionStorage.getItem(AUTO_RELOAD_MARKER)) || 0;
  } catch {
    // sessionStorage can be unavailable (private mode / blocked storage). We
    // lose the loop guard, not the update itself.
    return 0;
  }
}

export function markAutoReload(now: number): void {
  try {
    window.sessionStorage.setItem(AUTO_RELOAD_MARKER, String(now));
  } catch {
    // See readLastAutoReloadAt.
  }
}

/**
 * Whether anything on screen would be lost to a reload.
 *
 * `hasTyped` is tracked by the caller and cleared at every natural break. The
 * dialog probe covers the rest: WizardModal, SlideoutPanel, FloatingWindow and
 * the assistant widget all render `role="dialog"` only while open, so an open
 * one is a reliable "the user is in the middle of something" signal without
 * each of them having to report it.
 */
export function hasWorkInProgress(hasTyped: boolean): boolean {
  return hasTyped || document.querySelector('[role="dialog"]') !== null;
}
